import React from 'react';
import PriceBreakdown from './PriceBreakdown';

function calcEndTime(startTime, durationHours, extraHours = 0) {
  if (!startTime) return '—';
  const totalHours = parseInt(durationHours || 0) + parseInt(extraHours || 0);
  const [h, m] = startTime.split(':').map(Number);
  const totalMins = h * 60 + m + totalHours * 60;
  const endH = Math.floor(totalMins / 60) % 24;
  const endM = totalMins % 60;
  return `${String(endH).padStart(2, '0')}:${String(endM).padStart(2, '0')}`;
}

function formatTime(t) {
  if (!t) return '—';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  const hour = h % 12 || 12;
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
}

export default function BookingStep3({ form, pricing, booths, dbAddons = [], total, onBack, onSubmit, error }) {
  const formatDate = (d) => {
    if (!d) return '—';
    const [y, m, day] = d.split('-').map(Number);
    return new Date(y, m - 1, day).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  };
  
  // Find extra hours dynamically if any addon has 'hour' in the name
  let extraHours = 0;
  if (form.selected_addons && Array.isArray(form.selected_addons)) {
    form.selected_addons.forEach(item => {
      const addon = dbAddons.find(a => a.id === item.id);
      if (addon && addon.name.toLowerCase().includes('hour')) {
        extraHours = parseInt(item.quantity) || 0;
      }
    });
  }

  const endTime = calcEndTime(form.start_time, form.duration_hours, extraHours);
  const totalDuration = (parseInt(form.duration_hours) || 0) + extraHours;

  const addonsSelected = [];
  if (form.selected_addons && Array.isArray(form.selected_addons)) {
    form.selected_addons.forEach(item => {
      const addon = dbAddons.find(a => a.id === item.id);
      if (addon) {
        const qty = parseInt(item.quantity) || 1;
        const price = parseFloat(addon.price) || 0;
        if (addon.type === 'boolean') {
          addonsSelected.push(`${addon.name} (+$${price.toFixed(2)})`);
        } else {
          addonsSelected.push(`${addon.name} x${qty} (+$${(price * qty).toFixed(2)})`);
        }
      }
    });
  }

  const row = (label, value) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
      <span style={{ fontSize: 13, color: '#a0a0b8' }}>{label}</span>
      <span style={{ fontSize: 14, color: '#fff', fontWeight: 500, textAlign: 'right', maxWidth: '60%' }}>{value}</span>
    </div>
  );

  return (
    <div style={{ background: 'linear-gradient(135deg, #101428 0%, #0D0F20 100%)', borderRadius: 18, padding: 32, border: '1px solid rgba(227,67,100,0.3)' }}>
      <h2 style={{ margin: '0 0 4px', fontSize: 20, color: '#E34364', fontWeight: 700 }}>Review & Price</h2>
      <p style={{ margin: '0 0 28px', fontSize: 13, color: '#a0a0b8' }}>Confirm your details and see your live quote</p>

      {/* Event summary */}
      <div style={{ background: 'rgba(255,255,255,0.03)', borderRadius: 12, padding: '4px 16px', border: '1px solid rgba(255,255,255,0.06)', marginBottom: 24 }}>
        <div style={{ padding: '10px 0', borderBottom: '1px solid rgba(159,92,255,0.2)' }}>
          <span style={{ fontSize: 13, color: '#9F5CFF', fontWeight: 700 }}>📋 Event Summary</span>
        </div>
        {row('Name', form.full_name || '—')}
        {row('Phone', form.phone_number || '—')}
        {row('Event Date', formatDate(form.event_date))}
        {row('Time', form.start_time ? `${formatTime(form.start_time)} – ${formatTime(endTime)} (${totalDuration}h)` : '—')}
        {row('Strip Wordings', form.strip_wordings || '—')}
        {row('Booth Type', form.booth_type || '—')}
        {row('Photo Strip', form.photo_strip_style || '—')}
        {form.backdrop_name && row('Selected Backdrop', form.backdrop_name)}
        {form.event_theme_description && row('Event Theme', form.event_theme_description)}
        {addonsSelected.length > 0 && row('Add-ons', addonsSelected.join(' · '))}
      </div>

      {/* Live price breakdown */}
      <PriceBreakdown form={form} pricing={pricing} booths={booths} dbAddons={dbAddons} total={total} />

      {/* Payment note */}
      <div style={{ background: 'rgba(159,92,255,0.08)', border: '1px solid rgba(159,92,255,0.2)', borderRadius: 12, padding: '16px 20px', marginBottom: 24 }}>
        <p style={{ margin: '0 0 6px', fontSize: 13, color: '#9F5CFF', fontWeight: 700 }}>💳 Payment Options</p>
        <p style={{ margin: 0, fontSize: 13, color: '#e0e0e0', lineHeight: 1.6 }}>
          After signing your contract, you can choose to <strong>pay in full</strong> or pay <strong>50% now</strong> and the remaining 50% no later than 7 days before your event.
        </p>
      </div>

      {error && (
        <div style={{ background: 'rgba(227,67,100,0.1)', border: '1px solid #E34364', borderRadius: 12, padding: '12px 16px', marginBottom: 20, color: '#E34364', fontSize: 13, textAlign: 'center' }}>
          <strong>Submission Error:</strong> {error}
        </div>
      )}

      <div style={{ display: 'flex', gap: 12 }}>
        <button onClick={onBack} style={{ flex: 1, background: 'rgba(255,255,255,0.05)', color: '#fff', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 50, padding: '14px 0', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          ← Back
        </button>
        <button onClick={onSubmit} style={{ flex: 2, background: '#E34364', color: '#fff', border: 'none', borderRadius: 50, padding: '14px 0', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}>
          Submit & Sign Contract →
        </button>
      </div>
    </div>
  );
}
